import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import cx from "classnames";
import cn from "variant-classnames";
import { toolkitCx, toolkitTestIds } from "../../../constants";
const rootVariants = {
    $all: "flex w-full flex-col overflow-hidden",
};
const triggerVariants = {
    $all: cx("flex flex-1 items-center justify-center", "text-black/40 dark:text-gray-200/40", "hover:text-black dark:hover:text-gray-200", "radix-state-active:text-primary dark:radix-state-active:text-primary", toolkitCx.layout.transition.colors, toolkitCx.layout.background.hoverable),
    size: {
        sm: "text-sm py-1",
        md: "text-base py-2",
        lg: "text-lg py-3",
        xl: "text-lg py-4",
    },
};
/**
 * Tabs component that extends the functionality of Radix UI Tabs.
 * Provides a customizable and accessible tabs component with various appearance options.
 *
 * @component
 * @param {ITabsProps} props - The props for the Tabs component.
 * @param {"default"|"card"} [props.bg="card"] - The background color of the tabs.
 * @param {ITab[]} props.data - The data for the tabs, including triggers and content.
 * @param {IPrimitiveProps} [props.primitiveProps] - The props for customizing the underlying Radix UI TabsPrimitive components.
 * @param {TabsPrimitive.TabsContentProps} [props.primitiveProps.content] - The props for the Radix UI TabsPrimitive.TabsContent component.
 * @param {TabsPrimitive.TabsListProps} [props.primitiveProps.list] - The props for the Radix UI TabsPrimitive.TabsList component.
 * @param {TabsPrimitive.TabsTriggerProps} [props.primitiveProps.trigger] - The props for the Radix UI TabsPrimitive.TabsTrigger component.
 * @param {string} [props.className] - The className for the Tabs component.
 * @param {string} [props.contentTestId="tabs-content"] - The test id for the tooltip content.
 * @param {"sm"|"md"|"lg"|"xl"} [props.size="md"] - The size of the tabs.
 * @param {string} [props.triggerTestId="tabs-trigger"] - The test id for the tooltip trigger.
 * @param {"rounded"|"square"} [props.variant="rounded"] - The appearance variant of the tabs.
 * @param {string} props.value - The value of the currently selected tab.
 * @param {boolean} [props.withBlurryShadow] - DEPRECATED. Use `withBlurryCorner` instead.
 * @param {boolean} [props.withBlurryCorner] - Whether to apply a blurry corner to the tabs.
 * @param {TabsPrimitive.TabsProps} [props] - The additional props for the Radix UI TabsPrimitive.Tabs component.
 * @returns {React.Element} A rendered Tabs component.
 */
const Tabs = ({ bg = "card", className, contentTestId = toolkitTestIds.tabs.content, data, primitiveProps, size = "md", triggerTestId = toolkitTestIds.tabs.trigger, value, variant = "rounded", withBlurryCorner, withBlurryShadow, // TODO: remove deprecated `withBlurryShadow` once migrated
...props }) => {
    const rootClasses = cn(rootVariants, {
        className: cx(className, {
            "rounded-t-lg": (withBlurryShadow || withBlurryCorner) && variant === "rounded",
        }, {
            [`${toolkitCx.recipes.blurryCorner.primary}`]: withBlurryShadow || withBlurryCorner,
        }),
    });
    const triggerClasses = cn(triggerVariants, {
        className: cx("z-[2]", primitiveProps?.trigger?.className),
        size,
    });
    return (_jsxs(TabsPrimitive.Root, { ...props, className: rootClasses, value: value, children: [_jsxs(TabsPrimitive.List, { ...primitiveProps?.list, className: cx("relative flex shrink-0 p-[2px]", { "bg-white dark:bg-gray-900": bg === "default" }, { "bg-gray dark:bg-gray-800": bg === "card" }, {
                    "rounded-lg": variant === "rounded" && bg === "card",
                    "rounded-t-lg": variant === "rounded" && bg === "default",
                }, primitiveProps?.list?.className), children: [data.map(({ id, trigger }) => (_createElement(TabsPrimitive.Trigger, { ...primitiveProps?.trigger, key: id, value: id, className: triggerClasses, "data-testid": triggerTestId }, trigger))), _jsx("div", { className: cx("absolute bottom-0 left-0 z-[1] h-[calc(100%-8px)]", toolkitCx.recipes.center.vertical, {
                            "bg-gray/95 dark:bg-gray-800/95": bg === "default",
                            "bg-gray-200 dark:bg-gray-700": bg === "card",
                            "rounded-lg": variant === "rounded",
                        }), style: {
                            transition: "left 0.5s cubic-bezier(0.87, 0, 0.13, 1)",
                            left: `calc(calc(calc(100% / ${data.length}) * ${Number(value)}) + 4px)`,
                            width: `calc(calc(100% / ${data.length}) - 8px)`,
                        } })] }), data.map(({ id, content }) => (_createElement(TabsPrimitive.Content, { ...primitiveProps?.content, key: id, value: id, "data-testid": contentTestId, className: cx(primitiveProps?.content?.className, {
                    "z-10": withBlurryShadow || withBlurryCorner,
                }, {
                    "bg-white dark:bg-gray-900": bg === "default",
                }, {
                    "rounded-b-lg": variant === "rounded" && bg === "default",
                }) }, content)))] }));
};
export default Tabs;
//# sourceMappingURL=Tabs.js.map